<script setup>
import { computed, ref } from 'vue';
import {
    FileTextOutlined,
    UserOutlined,
    DownOutlined,
    RightOutlined
} from '@ant-design/icons-vue';
import { getBalanceSheetSourceMeta } from '@/utils/balanceSheetDisplay';

const props = defineProps({
    versionInfoForm: { type: Object, required: true },
    udvForm: { type: Object, required: true },
    versionName: { type: String, default: '' },
    /** 0=自行上傳 / 1=盤點 / 2=諮詢 */
    versionSource: { type: [Number, String], default: 0 },
    isReadonly: { type: Boolean, default: false },
    versionLoading: { type: Boolean, default: false },
    personalLoading: { type: Boolean, default: false }
});

const emit = defineEmits(['save']);
const collapsed = ref(true);

const versionSourceLabel = computed(
    () => getBalanceSheetSourceMeta(props.versionSource).label
);
</script>

<template>
    <a-card :bordered="false" :class="['admin-panel', 'admin-profile-card', { 'is-collapsed': collapsed }]">
        <div class="admin-profile-card__header">
            <button
                type="button"
                class="admin-profile-card__toggle"
                :aria-expanded="!collapsed"
                @click="collapsed = !collapsed"
            >
                <span class="admin-profile-card__chevron" aria-hidden="true">
                    <RightOutlined v-if="collapsed" />
                    <DownOutlined v-else />
                </span>
                <span>
                    <span class="admin-profile-card__title">基本資料</span>
                    <span v-if="versionName" class="admin-profile-card__subtitle">
                        目前版本：{{ versionName }}
                    </span>
                </span>
            </button>
            <a-button
                v-if="!isReadonly && !collapsed"
                type="primary"
                class="admin-editor-btn admin-editor-btn--primary"
                :loading="versionLoading || personalLoading"
                @click="emit('save')"
            >
                儲存基本資料
            </a-button>
        </div>

        <div v-show="!collapsed" class="admin-profile-card__body">
            <div class="admin-profile-card__grid">
                <section class="admin-profile-card__section">
                    <div class="admin-profile-card__section-head">
                        <FileTextOutlined />
                        <span>版本設定</span>
                    </div>
                    <div class="admin-profile-card__fields">
                        <label class="admin-profile-card__field">
                            <span>內部名稱</span>
                            <a-input
                                v-model:value="versionInfoForm.internal_name"
                                :disabled="isReadonly"
                                placeholder="管理用名稱"
                            />
                        </label>
                        <label class="admin-profile-card__field">
                            <span>外部名稱</span>
                            <a-input
                                v-model:value="versionInfoForm.external_name"
                                :disabled="isReadonly"
                                placeholder="學員看到的名稱"
                            />
                        </label>
                        <label class="admin-profile-card__field">
                            <span>版本來源</span>
                            <a-input :value="versionSourceLabel" disabled />
                        </label>
                    </div>
                </section>

                <section class="admin-profile-card__section">
                    <div class="admin-profile-card__section-head">
                        <UserOutlined />
                        <span>學員資料</span>
                    </div>
                    <div class="admin-profile-card__fields">
                        <label class="admin-profile-card__field">
                            <span>年齡</span>
                            <a-input-number
                                v-model:value="udvForm.age"
                                :min="0"
                                :max="120"
                                :disabled="personalLoading || isReadonly"
                                class="w-full"
                            />
                        </label>
                        <label class="admin-profile-card__field">
                            <span>職業</span>
                            <a-input
                                v-model:value="udvForm.job"
                                :disabled="personalLoading || isReadonly"
                                placeholder="請輸入職業"
                            />
                        </label>
                        <label class="admin-profile-card__field">
                            <span>婚姻狀態</span>
                            <a-select
                                v-model:value="udvForm.marital_status"
                                :disabled="personalLoading || isReadonly"
                                class="w-full"
                            >
                                <a-select-option :value="0">未婚</a-select-option>
                                <a-select-option :value="1">已婚</a-select-option>
                            </a-select>
                        </label>
                        <label class="admin-profile-card__field">
                            <span>子女數</span>
                            <a-input-number
                                v-model:value="udvForm.children"
                                :min="0"
                                :max="20"
                                :disabled="personalLoading || isReadonly"
                                class="w-full"
                            />
                        </label>
                    </div>
                </section>
            </div>
        </div>
    </a-card>
</template>

<style scoped>
.admin-profile-card {
    margin-bottom: 12px;
}

.admin-profile-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.admin-profile-card:not(.is-collapsed) .admin-profile-card__header {
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
}

.admin-profile-card__toggle {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
}

.admin-profile-card__chevron {
    display: inline-flex;
    align-items: center;
    margin-top: 3px;
    color: #94a3b8;
    font-size: 11px;
}

.admin-profile-card__title {
    display: block;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
}

.admin-profile-card__subtitle {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
}

.admin-profile-card__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.admin-profile-card__section {
    padding: 12px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.admin-profile-card__section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 13px;
    font-weight: 700;
    color: #334155;
}

.admin-profile-card__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 12px;
}

.admin-profile-card__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.admin-profile-card__field > span {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
}

.admin-profile-card__field--switch {
    grid-column: 1 / -1;
}

.admin-profile-card__switch-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 32px;
}

.admin-profile-card__switch-hint {
    font-size: 13px;
    color: #475569;
}

@media (max-width: 900px) {
    .admin-profile-card__grid,
    .admin-profile-card__fields {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
